import { NetworkStatus, useReactiveVar } from "@apollo/client";
import {
  useGlobalSearchParams,
  useFocusEffect,
  usePathname,
} from "expo-router";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Dimensions, useWindowDimensions } from "react-native";

import { useInfiniteScroll } from "@/hooks/useInfiniteScroll";

import MainView from "./MainView";

import FilterResults from "@/components/organisms/FilterResults";
import { AnalyticsTracker } from "@/features/analytics";
import { filterValuesVar } from "@/features/player-filter";
import {
  useRosterManagementListMyOrganizationsPlayersQuery,
  useWatchlistListMyUserWatchlistsQuery,
} from "@/generated/graphql";
import { MY_WATCHLISTS_PAGE_SIZE } from "@/features/watchlist/constants";
import { useCustomMetricSortVariables } from "@/hooks/useCustomMetricSortVariables";
import useMobileScreen from "@/hooks/useMobileScreen";
import useSetPageTitle from "@/hooks/useSetPageTitle";
import { useCanAccessFeature } from "@/providers/FeatureFlags";
import { useTeamSelection } from "@/providers/TeamSelectionProvider";
import {
  isFocusSearchInputVar,
  searchTextVar,
} from "@/stores/isFocusSearchInput";
import { playerDetailVar, suppressAutoSelectVar } from "@/stores/player";
import { watchlistSortVar } from "@/stores/selectionSort";

/** Minimum viewport width (px) treated as tablet-sized for the header search bar */
const TABLET_MIN_WIDTH = 768;

/**
 * Main Container for Roster Management
 *
 * Orchestrates the primary roster view with player listing, filtering, sorting,
 * and watchlist integration. Coordinates between filter state, custom metrics
 * sorting, and the player detail panel for a responsive three-column layout on
 * larger screens and filtered results navigation on mobile.
 * @module features/roster-management/screens/Main/MainContainer
 */
const MainContainer = () => {
  const isSortPlayerEnable = useCanAccessFeature("globalSort");
  const isFocusSearchInput = useReactiveVar(isFocusSearchInputVar);
  const filterValues = useReactiveVar(filterValuesVar);
  const sortValues = useReactiveVar(watchlistSortVar);
  const isSmallScreen = useMobileScreen();
  const pathname = usePathname();
  const { width } = useWindowDimensions();
  const isTabletWidth = width >= TABLET_MIN_WIDTH;
  const params = useGlobalSearchParams();
  const { playerId: urlPlayerId } = params;
  const hasAutoSelectedRef = useRef(false);
  // SE-4010: keeps pathname-churn from re-firing roster_viewed (see effect
  // below).
  const hasLoggedRosterViewRef = useRef(false);
  const [autoSelectedPlayerId, setAutoSelectedPlayerId] = useState<
    string | undefined
  >(undefined);

  // URL playerId takes precedence (shared links), then auto-selected
  const playerId = (urlPlayerId as string) || autoSelectedPlayerId;

  // Get custom metric sort variables using shared hook
  const { sortVariables } = useCustomMetricSortVariables(
    sortValues,
    isSortPlayerEnable
  );

  const { isLoaded: isTeamSelectionLoaded } = useTeamSelection();

  const queryVariables = useMemo(() => {
    return {
      // Use shared sort variables from hook
      ...sortVariables,
      // Apply position filters
      positionIds: filterValues?.positions?.map?.(position => position.id),
    };
  }, [sortVariables, filterValues]);

  const { data, fetchMore, refetch, loading, networkStatus } =
    useRosterManagementListMyOrganizationsPlayersQuery({
      variables: queryVariables,
      fetchPolicy: "cache-and-network",
      nextFetchPolicy: "cache-and-network",
      notifyOnNetworkStatusChange: true,
      skip: !isTeamSelectionLoaded,
      // Never coalesce the roster list — same reason as the player-detail
      // sidebar above it in this feature: it is the query this screen's
      // content depends on, and riding the boot batch makes it share one
      // 25s abort with nine reference-data queries it does not need
      // (SE-6233's `AuthMe` precedent).
      context: { batch: false },
    });
  const { data: watchlistData } = useWatchlistListMyUserWatchlistsQuery({
    variables: { first: MY_WATCHLISTS_PAGE_SIZE },
    fetchPolicy: "cache-and-network",
    // Defer until team selection is hydrated so this list query does not fire
    // on mount before the screen needs it (avoids a premature refetch). The
    // sidebar list still renders from this same data once loaded.
    skip: !isTeamSelectionLoaded,
  });

  const isRefreshing = networkStatus === NetworkStatus.refetch;
  const isHaveFilter =
    Boolean(filterValues) && Object.keys(filterValues || {}).length > 0;

  const watchlists = useMemo(
    () =>
      watchlistData?.listMyUserWatchlists?.edges?.map(
        edge => edge?.node?.watchlist
      ) ?? [],
    [watchlistData?.listMyUserWatchlists?.edges]
  );

  const players = useMemo(() => {
    return (
      data?.listMyOrganizationsPlayers?.edges?.map(edge => edge?.node) ?? []
    );
  }, [data]);

  const isOnWatchlistTab = useMemo(() => {
    return pathname === "/watchlist";
  }, [pathname]);

  const { loadMore: handleLoadMore } = useInfiniteScroll({
    pageInfo: data?.listMyOrganizationsPlayers?.pageInfo,
    fetchMore,
    networkStatus,
    extraVariables: queryVariables,
  });

  // Apollo's `refetch()` can bypass the `skip` flag in some versions
  // (apollographql/apollo-client#8270, #9101) and trigger an org-wide
  // request before team selection has hydrated. Mirror the skip check
  // here so user-initiated and focus-triggered refetches stay consistent
  // with the auto-skip on the underlying query.
  const handleRefresh = useCallback(() => {
    if (!isTeamSelectionLoaded) return;
    refetch(queryVariables).catch(() => {});
  }, [isTeamSelectionLoaded, refetch, queryVariables]);

  const infoTemp = useReactiveVar(playerDetailVar);
  const playerDetail = infoTemp?.player;

  const typeRightColumn = useMemo(() => {
    if (!playerDetail) {
      return "recommendation";
    }
    return "note";
  }, [playerDetail]);

  // Use FilterResults for all filters on small screens (not on watchlist tab)
  const shouldUseFilterResults =
    isHaveFilter && isSmallScreen && !isOnWatchlistTab;

  // Clear playerDetailVar when navigating away from a player (playerId becomes undefined)
  // This ensures the right panel switches back to "recommendation" when no player is selected
  useEffect(() => {
    if (!playerId && playerDetail) {
      playerDetailVar(undefined);
    }
  }, [playerId, playerDetail]);

  // SE-4010: view tracking for the roster surface. This container serves
  // multiple routes (e.g. /player-roster and the small-screen watchlist
  // tab), so source records which pathname rendered it. Once per mount.
  useEffect(() => {
    if (hasLoggedRosterViewRef.current) return;
    hasLoggedRosterViewRef.current = true;
    void AnalyticsTracker.surface.logRosterViewed({ source: pathname });
  }, [pathname]);

  // Auto-select first player on desktop when no player is selected.
  // Uses local state instead of router.replace() to avoid poisoning the
  // navigation state with a URL query param that prevents tab switching
  // via router.navigate() from the sidebar (SE-3726).
  // The suppress flag is consumed eagerly (regardless of loading state) and
  // sets the ref to permanently block auto-select for this mount cycle,
  // surviving any subsequent refetch-triggered re-runs of this effect.
  useEffect(() => {
    if (suppressAutoSelectVar()) {
      suppressAutoSelectVar(false);
      hasAutoSelectedRef.current = true;
      return;
    }

    const firstPlayer = players[0];
    const shouldAutoSelect =
      !loading &&
      !playerId &&
      !isSmallScreen &&
      firstPlayer?.id &&
      !hasAutoSelectedRef.current;

    if (shouldAutoSelect) {
      hasAutoSelectedRef.current = true;

      // Defer to avoid calling setState synchronously inside an effect
      // (react-hooks/set-state-in-effect).
      queueMicrotask(() => setAutoSelectedPlayerId(firstPlayer.id));
    }
  }, [loading, playerId, players, isSmallScreen]);

  // Reset auto-selection when navigating away so it can re-trigger on return
  useFocusEffect(
    useCallback(() => {
      return () => {
        hasAutoSelectedRef.current = false;
        setAutoSelectedPlayerId(undefined);
      };
    }, [])
  );

  useSetPageTitle("Organization Roster");

  // Refetch data when component regains focus (e.g., after back navigation from Kanban).
  // Same skip-bypass guard as handleRefresh above.
  useFocusEffect(
    useCallback(() => {
      if (!isTeamSelectionLoaded) return;
      refetch(queryVariables).catch(() => {});
    }, [isTeamSelectionLoaded, refetch, queryVariables])
  );

  useFocusEffect(
    useCallback(() => {
      if (isFocusSearchInput) {
        isFocusSearchInputVar(false);
        searchTextVar("");
      }
    }, [isFocusSearchInput])
  );

  return shouldUseFilterResults ? (
    <FilterResults />
  ) : (
    <MainView
      players={players}
      loading={loading}
      refreshing={isRefreshing}
      watchlists={watchlists}
      isSmallScreen={isSmallScreen}
      isTabletWidth={isTabletWidth}
      isOnWatchlistTab={isOnWatchlistTab}
      onLoadMore={handleLoadMore}
      onRefresh={handleRefresh}
      heightRecommendation={Dimensions.get("window").height * 0.2}
      type={typeRightColumn}
      playerId={playerId as string}
      playerDetail={playerDetail}
    />
  );
};

export default MainContainer;
